import { Building, Home, MapPin, Phone } from "lucide-react";
import type { PropertyRecord } from "@shyne/shared/api";
import MainLayout from "@/components/layouts";
import { Image, PLACEHOLDER_IMAGE } from "@/components/shared/image-fallback";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import type { uuid } from "@/interfaces";
import { getInitials } from "@/lib/utils.ts";
import { ContactAppointmentsTable } from "./appointments-table";
import { useContactProperty } from "./contact.hooks";

function Stat({ label, value }: { label: string; value: number | null | undefined }) {
  return (
    <div className="text-center p-3 bg-muted rounded-lg">
      <div className="font-semibold text-lg">{value ?? "—"}</div>
      <div className="text-sm text-muted-foreground">{label}</div>
    </div>
  );
}

function OnSiteContact({ contact }: { contact: NonNullable<PropertyRecord["onSiteContact"]> }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">On-site contact</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-3">
          <Avatar className="h-12 w-12">
            <AvatarFallback>{getInitials(contact.name)}</AvatarFallback>
          </Avatar>
          <span className="font-semibold">{contact.name}</span>
        </div>
        {contact.phone && (
          <p className="flex items-center gap-2 text-sm">
            <Phone className="h-4 w-4" />
            {contact.phone}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function PropertyRecordView({ property }: { property: PropertyRecord }) {
  const { details } = property;
  return (
    <div className="container mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-9 space-y-6">
          <Card className="overflow-hidden p-0">
            <Image src={details?.image ?? PLACEHOLDER_IMAGE} alt={property.name} className="w-full max-h-96 object-cover" />
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-2xl mb-2">{property.name}</CardTitle>
              {property.address && (
                <div className="flex items-center text-muted-foreground">
                  <MapPin className="h-4 w-4 mr-1" />
                  <span>{property.address}</span>
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                {details?.types.map((type) => (
                  <Badge key={type} variant="secondary" className="flex items-center gap-1">
                    <Building className="h-3 w-3" />
                    {type}
                  </Badge>
                ))}
                {details?.structures.map((structure) => (
                  <Badge key={structure} variant="secondary" className="flex items-center gap-1">
                    <Home className="h-3 w-3" />
                    {structure}
                  </Badge>
                ))}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {details?.description && <p className="text-muted-foreground leading-relaxed">{details.description}</p>}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Stat label="Total Units" value={property.unitCount} />
                <Stat label="Floors" value={details?.floors} />
                <Stat label="Appointments" value={property.appointmentCount} />
                <Stat label="Year Built" value={details?.yearBuilt} />
              </div>
            </CardContent>
          </Card>

          <ContactAppointmentsTable property={property.id} title="Appointments" description={`Appointments at ${property.name}`} />
        </div>

        <div className="lg:col-span-3 space-y-6">{property.onSiteContact && <OnSiteContact contact={property.onSiteContact} />}</div>
      </div>
    </div>
  );
}

export function ContactPropertyView({ propertyId }: { propertyId: uuid }) {
  const { value: property, loading, notFound, error } = useContactProperty(propertyId);

  if (loading || (!property && !notFound && !error))
    return (
      <MainLayout breadcrumb={[{ name: "Loading...", href: "#" }]}>
        <div className="container mx-auto space-y-6">
          <Skeleton className="h-96 w-full rounded-lg" />
          <Skeleton className="h-[300px] w-full rounded-lg" />
        </div>
      </MainLayout>
    );

  if (!property)
    return (
      <MainLayout breadcrumb={[{ name: "Property", href: "#" }]}>
        <p className="text-destructive">{notFound ? "Property not found" : error}</p>
      </MainLayout>
    );

  return (
    <MainLayout breadcrumb={[{ name: property.name, href: "#" }]}>
      <PropertyRecordView property={property} />
    </MainLayout>
  );
}
